import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { createCartProduct } from "../../../../shared/hooks/useFetchServiceDetails";
import API_URLS from "../../Utils/axr";
import store from "../store";
import { PURGE } from "redux-persist";
import { getForIQRemove } from "../../Utils/util-functions";
import DwLogutil from "../../Utils/DwLogutil";
import { EC_DW_LOGGING_STEPS } from "../../../../shared/utils/Constants";

interface MoversCartState {
  moversCartResponse: any;
  loading: boolean;
  prevCartResponse: any;
  moversCreateCartResponse: any; // Stores ONLY the createCart response, never overwritten by updateCart
  error: string | null;

  moversHasThts: any;
  moversThtsCheckbox: any;
  moversTermsAndCondition: any;
  moversPreferenceConfirmation: boolean | null;
}

const initialState: MoversCartState = {
  moversCartResponse: null,
  loading: false,
  error: null,
  prevCartResponse: null,
  moversCreateCartResponse: null,

  moversHasThts: false,
  moversThtsCheckbox: false,
  moversTermsAndCondition: false,
  moversPreferenceConfirmation: null,
};

// Async thunk for creating cart in movers flow
export const createMoversCart = createAsyncThunk(
  "moversCart/createCart",
  async (
    {
      listItems,
      voiceRemoved = false,
      iQremoved = false,
      productType = "",
      optionalFeatureList = [],
    }: {
      listItems: any;
      voiceRemoved?: boolean;
      iQremoved?: boolean;
      productType?: string;
      optionalFeatureList?: any[];
    },
    { rejectWithValue }
  ) => {
    try {
      const reduxStore = store.getState();

      // Get movers address data for movers flow
      const moversAddressQualificationData =
        reduxStore?.addressQualification?.moversData?.moversAddressQualification
          ?.entity;

      const headers = reduxStore?.brightspeedToken?.headers;

      const wireCenter = moversAddressQualificationData?.wireCenter;
      const transactionId =
        moversAddressQualificationData?.partnerSessionId ||
        "brspd-" + headers?.sessionId;
      const siteId = moversAddressQualificationData?.siteId || headers?.siteId;
      const addressId = moversAddressQualificationData?.addressId;

      const channel = "web-acm";
      const moversInternetProductData = reduxStore?.moversInternetProductData?.moversInternetProductData?.productResponse?.productDetails;
       const currentProductData = moversInternetProductData.find(
          (plan: any) => plan?.offerResponse?.offerDetails?.dynamicPrice === true
        ) 
      

      const affinityDiscountApplicable =
        reduxStore?.planDetails?.planDetails &&
        reduxStore?.planDetails?.planDetails?.discountPlans?.find(
          (affinityApplicable: any) =>
            affinityApplicable?.productName?.includes("Affinity Discount")
        );
      const internetExists = reduxStore?.InternetProductsState?.internetExist;

      let url_new = API_URLS["createCart"];

      const cartProductPostData = {
        baseline: {
          transactionId,
          channel: channel,
          wirecenter: wireCenter,
          workflow: "mover",
          siteId,
          billingType: "postpaid",
          partnerId: "BS196123",
          salesCode: "20000660",
          partnerOrderId: transactionId,
        },

        currency: "USD",

        cartId: "",
        ...(currentProductData?.offerResponse?.offerDetails?.dynamicPrice && { dynamicPrice: true, currentPlanPrice: currentProductData?.offerResponse?.offerDetails?.priceResult?.[0]?.productCharge }),

        lineItems: listItems,
        ...(listItems.filter((item: any) => item.itemActionType === "remove")
          .length > 0 && { cartAddOnsAction: "remove" }),
        ...(voiceRemoved && { cartAddOnsAction: "remove" }),
        ...(localStorage.getItem("iqRemoved") && { cartAddOnsAction: "remove" }),
        ...(productType && { productType: productType }),

        shippingAddress: {
          id: addressId,
          streetName: moversAddressQualificationData?.address,
          additionalStreetInfo: "",
          postalCode: moversAddressQualificationData?.postalCode,
          city: moversAddressQualificationData?.city,
          state: moversAddressQualificationData?.state,
          key: moversAddressQualificationData?.fullAddress,
          country: "US",
          wirecenter: wireCenter,
        },

        ...(affinityDiscountApplicable &&
        !internetExists &&
        (affinityDiscountApplicable?.affinityOccupation === "Student" ||
          affinityDiscountApplicable?.affinityOccupation === "Teacher" ||
          affinityDiscountApplicable?.affinityOccupation === "Military")
          ? {
              customerGroup: {
                key: affinityDiscountApplicable?.affinityOccupation,
                typeId: "customer-group",
              },
            }
          : {
              customerGroup: {
                key: "BS-Normal-User",
                typeId: "customer-group",
              },
            }),
      };

      const params = {
        "": [url_new],
      };

      const productsSummary = JSON.stringify(
        listItems?.map((item: any) => ({
          productName: item?.productName,
          itemActionType: item?.itemActionType,
          productCode: item?.productCode,
        }))
      );

      DwLogutil({ stepName: EC_DW_LOGGING_STEPS.movers_create_cart_api_triggered, message: productsSummary });

      const response = await createCartProduct(
        params,
        cartProductPostData,
        headers
      );

      const messageStatus = response?.data?.messageStatus?.find(
        (item: any) => "statusCode" in item
      );

      // Handle movers flow specific error
      const responseCartId = response?.data?.cartResponse?.cartId;
      if (!responseCartId || messageStatus?.statusCode !== "200") {
        DwLogutil({
          stepName: EC_DW_LOGGING_STEPS.movers_create_cart_api_failed,
          error: messageStatus?.statusMessage || "Cart creation failed for movers flow",
          message: productsSummary,
        });
        return rejectWithValue({
          message: "Cart creation failed for movers flow",
          statusCode: messageStatus?.statusCode || "500",
          response: response?.data,
        });
      }

      if (messageStatus?.statusCode === "200") {
        DwLogutil({ stepName: EC_DW_LOGGING_STEPS.movers_create_cart_api_success, message: productsSummary });
        return response?.data?.cartResponse;
      } else {
        DwLogutil({
          stepName: EC_DW_LOGGING_STEPS.movers_create_cart_api_failed,
          error: response?.data?.messageStatus?.[0]?.statusMessage,
          message: productsSummary,
        });
        return rejectWithValue(response.data);
      }
    } catch (error: any) {
      DwLogutil({
        stepName: EC_DW_LOGGING_STEPS.movers_create_cart_api_failed,
        error: error?.message || error.toString(),
        message: productsSummary,
      });
      return rejectWithValue(error.toString());
    }
  }
);

// Async thunk for updating cart in movers flow
export const updateMoversCart = createAsyncThunk(
  "moversCart/updateCart",
  async (
    {
      listItems,
      cartId,
      voiceRemoved = false,
      iQremoved = false,
      productType = "",
      optionalFeatureList,
    }: {
      listItems: any;
      cartId: string;
      voiceRemoved?: boolean;
      iQremoved?: boolean;
      productType?: string;
      optionalFeatureList?: any;
    },
    { rejectWithValue }
  ) => {
    try {
      const reduxStore = store.getState();

      // Get movers address data for movers flow
      const moversAddressQualificationData =
        reduxStore?.addressQualification?.moversData?.moversAddressQualification
          ?.entity;

      const headers = reduxStore?.brightspeedToken?.headers;

      const wireCenter = moversAddressQualificationData?.wireCenter;
      const transactionId = moversAddressQualificationData?.partnerSessionId;
      const siteId = moversAddressQualificationData?.siteId || headers?.siteId;

      const channel = "web-acm";

      const affinityDiscountApplicable =
        reduxStore?.planDetails?.planDetails &&
        reduxStore?.planDetails?.planDetails?.discountPlans?.find(
          (affinityApplicable: any) =>
            affinityApplicable?.productName?.includes("Affinity Discount")
        );
      const affinityOccupation =
        affinityDiscountApplicable &&
        affinityDiscountApplicable?.affinityOccupation;
      const internetExists = reduxStore?.InternetProductsState?.internetExist;
      const user = reduxStore?.user?.data;

      const cartProductPostData = {
        baseline: {
          transactionId: transactionId,
          channel: channel,
          wirecenter: wireCenter,
          workflow: "mover",
          siteId,
          partnerOrderId: transactionId,
          billingType: "postpaid",
          partnerId: "BS196123",
          salesCode: "20000660",
        },

        cartId: cartId,

        actions: listItems,

        ...(listItems.filter((item: any) => item.itemActionType === "remove")
          .length > 0 && { cartAddOnsAction: "remove" }),
        ...(voiceRemoved && { cartAddOnsAction: "remove" }),
        ...(localStorage.getItem("iqRemoved") && { cartAddOnsAction: "remove" }),
        ...(productType && { productType: productType }),
        ...(optionalFeatureList && { optionalFeatureList: optionalFeatureList }),

        discountType:
          affinityDiscountApplicable && !internetExists ? "Affinity" : "",
        ...(affinityDiscountApplicable &&
        !internetExists &&
        (affinityDiscountApplicable?.affinityOccupation === "Student" ||
          affinityDiscountApplicable?.affinityOccupation === "Teacher" ||
          affinityDiscountApplicable?.affinityOccupation === "Military")
          ? {
              customerGroup: {
                key: affinityOccupation,
                typeId: "customer-group",
              },
            }
          : {
              customerGroup: {
                key: "BS-Normal-User",
                typeId: "customer-group",
              },
            }),
        wirecenter: wireCenter,
      };

      let url = API_URLS?.updateCart;
      const params = {
        "": [url],
      };

      const productsSummary = JSON.stringify(
        listItems?.map((item: any) => ({
          productName: item?.productName,
          itemActionType: item?.itemActionType,
          productCode: item?.productCode,
        }))
      );

      DwLogutil({ stepName: EC_DW_LOGGING_STEPS.movers_update_cart_api_triggered, message: productsSummary });

      const response = await createCartProduct(
        params,
        cartProductPostData,
        headers
      );

      const messageStatus = response?.data?.messageStatus?.find(
        (item: any) => "statusCode" in item
      );

      // Handle movers flow specific error
      const responseCartId = response?.data?.cartResponse?.cartId;
      if (!responseCartId || messageStatus?.statusCode !== "200") {
        DwLogutil({
          stepName: EC_DW_LOGGING_STEPS.movers_update_cart_api_failed,
          error: messageStatus?.statusMessage || "Cart update failed for movers flow",
          message: productsSummary,
        });
        return rejectWithValue({
          message: "Cart update failed for movers flow",
          statusCode: messageStatus?.statusCode || "500",
          response: response?.data,
        });
      }

      if (messageStatus?.statusCode === "200") {
        DwLogutil({ stepName: EC_DW_LOGGING_STEPS.movers_update_cart_api_success, message: productsSummary });
        return response?.data?.cartResponse;
      } else {
        DwLogutil({
          stepName: EC_DW_LOGGING_STEPS.movers_update_cart_api_failed,
          error: response?.data?.messageStatus?.[0]?.statusMessage,
          message: productsSummary,
        });
        return rejectWithValue(response.data);
      }
    } catch (error: any) {
      DwLogutil({
        stepName: EC_DW_LOGGING_STEPS.movers_update_cart_api_failed,
        error: error?.message || error.toString(),
        message: productsSummary,
      });
      return rejectWithValue(error.toString());
    }
  }
);

// Movers Cart slice
const moversCartSlice = createSlice({
  name: "moversCart",
  initialState,
  reducers: {
    resetMoversCartState: (state) => {
      state.moversCartResponse = null;
      state.loading = false;
      state.error = null;
      state.prevCartResponse = null;
      state.moversCreateCartResponse = null;
    },
    updateMoversCartResponseManually: (state, action) => {
      state.moversCartResponse = action.payload;
    },
    setMoversHasThts: (state, action) => {
      state.moversHasThts = action.payload;
    },
    setMoversThtsCheckbox: (state, action) => {
      state.moversThtsCheckbox = action.payload;
    },
    setMoversTermsAndCondition: (state, action) => {
      state.moversTermsAndCondition = action.payload;
    },
    setMoversPreferenceConfirmation: (state, action) => {
      state.moversPreferenceConfirmation = action.payload;
    },
  },
  extraReducers: (builder) => {
    // createMoversCart
    builder.addCase(createMoversCart.pending, (state) => {
      state.loading = true;
      state.error = null;
    });

    builder.addCase(
      createMoversCart.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.moversCartResponse = action.payload;
        state.prevCartResponse = action.payload;
        state.moversCreateCartResponse = action.payload; // Preserve original createCart response
      }
    );

    builder.addCase(createMoversCart.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // updateMoversCart
    builder.addCase(updateMoversCart.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.prevCartResponse = state.moversCartResponse;
    });

    builder.addCase(
      updateMoversCart.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.loading = false;
        // fix - check for remove THIC is exist in prev response and not next call
        let _resp = getForIQRemove(state.prevCartResponse, action.payload);
        state.moversCartResponse = _resp;
      }
    );

    builder.addCase(updateMoversCart.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    builder.addCase(PURGE, () => {
      return initialState;
    });
  },
});

export const { resetMoversCartState, updateMoversCartResponseManually, setMoversThtsCheckbox, setMoversTermsAndCondition, setMoversHasThts, setMoversPreferenceConfirmation } =
  moversCartSlice.actions;

export default moversCartSlice.reducer;
